/* RTDB records store dates as ISO strings, epoch millis, epoch seconds, or
 * nothing at all depending on which version of the app wrote them. Everything
 * here degrades to a dash rather than throwing or printing "Invalid Date". */

export function toDate(value?: string | number | null): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') {
    // Heuristic: anything below ~1e11 is seconds, not milliseconds.
    return new Date(value < 1e11 ? value * 1000 : value)
  }
  const n = Number(value)
  if (!Number.isNaN(n) && value.trim() !== '') {
    return new Date(n < 1e11 ? n * 1000 : n)
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatDate(value?: string | number | null): string {
  const d = toDate(value)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatDateTime(value?: string | number | null): string {
  const d = toDate(value)
  if (!d) return '—'
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function relativeTime(value?: string | number | null): string {
  const d = toDate(value)
  if (!d) return '—'
  const secs = Math.round((d.getTime() - Date.now()) / 1000)
  const abs = Math.abs(secs)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < 60) return rtf.format(Math.round(secs), 'second')
  if (abs < 3600) return rtf.format(Math.round(secs / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(secs / 3600), 'hour')
  if (abs < 2592000) return rtf.format(Math.round(secs / 86400), 'day')
  if (abs < 31536000) return rtf.format(Math.round(secs / 2592000), 'month')
  return rtf.format(Math.round(secs / 31536000), 'year')
}

export function formatNumber(value?: number | string | null, digits = 0): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })
}

/** Kilograms, shown as tonnes once the number stops being readable in kg. */
export function formatMass(kg?: number | string | null): string {
  if (kg === null || kg === undefined || kg === '') return '—'
  const n = typeof kg === 'number' ? kg : Number(kg)
  if (Number.isNaN(n)) return '—'
  return n >= 1000 ? `${formatNumber(n / 1000, 1)} t` : `${formatNumber(n)} kg`
}

export function titleCase(s?: string): string {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Up to two initials for an avatar fallback, from a name or (failing that) an email. */
export function initials(name?: string, email?: string): string {
  const src = (name ?? '').trim() || (email ?? '').split('@')[0] || '?'
  const parts = src.split(/[\s._-]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || src[0].toUpperCase()
}
